import type {AnalyticsPersonProperties} from "@mytour/shared";
import {isEmptyVatId, isLegalInfoComplete, isReadyToInvoice} from "@mytour/shared";
import {useEffect, useRef} from "react";
import {useAccount} from "./account/query/accountQueries.js";
import {useCurrentCompany, useCurrentUserStyle} from "./account/query/platformUserQueries.js";
import {analyticsService} from "./analytics.service.js";
import {useModeration} from "./authentication/authenticationQueries.js";

/**
 * Analytics session watcher — the single emission point of the frontend
 * onboarding funnel (#467).
 *
 * Instead of wiring each mutation site (customer creation, service creation,
 * four legal-settings mutations, logo upload...), the watcher observes the
 * session-global queries and emits a milestone exactly when its signal flips.
 * Every contributing mutation already refetches/invalidates those queries
 * (`refetchSession()` after customer/service creation, `platformUser` root
 * invalidation after legal/logo updates), so the flip is observed right after
 * the action — and a signal that is already true at the first snapshot never
 * re-emits, which makes the funnel idempotent across sessions.
 */

/**
 * Funnel signals derived from the session-global queries. `hasLogo` is
 * tri-state: `null` while the user-style query has not answered — the other
 * signals must not wait for it (a slow or failed style request would
 * otherwise silently kill the whole funnel baseline).
 */
export interface OnboardingSnapshot {
  customers: number;
  serviceDescriptions: number;
  legalComplete: boolean;
  hasLogo: boolean | null;
  readyToInvoice: boolean;
}

export type OnboardingMilestoneEvent =
  | "onboarding_first_customer_created"
  | "onboarding_first_service_created"
  | "onboarding_legal_completed"
  | "onboarding_logo_uploaded"
  | "onboarding_ready_to_invoice";

/**
 * Pure transition detector: returns the milestones whose signal flipped from
 * false to true between two snapshots. The first snapshot is the baseline —
 * no event, so signals already acquired (past sessions) never re-emit.
 */
export function computeOnboardingMilestones(
  prev: OnboardingSnapshot | null,
  next: OnboardingSnapshot
): OnboardingMilestoneEvent[] {
  if (prev === null) {
    return [];
  }
  const events: OnboardingMilestoneEvent[] = [];
  if (prev.customers === 0 && next.customers > 0) {
    events.push("onboarding_first_customer_created");
  }
  if (prev.serviceDescriptions === 0 && next.serviceDescriptions > 0) {
    events.push("onboarding_first_service_created");
  }
  if (!prev.legalComplete && next.legalComplete) {
    events.push("onboarding_legal_completed");
  }
  // Strict false → true only: a `null` baseline (style query still pending
  // when the first snapshot was taken) must not fake an upload when the
  // style finally answers with a pre-existing logo.
  if (prev.hasLogo === false && next.hasLogo === true) {
    events.push("onboarding_logo_uploaded");
  }
  if (!prev.readyToInvoice && next.readyToInvoice) {
    events.push("onboarding_ready_to_invoice");
  }
  return events;
}

/**
 * Handoff flag between AutoLoginPage and the watcher: the page marks the
 * autologin success, the watcher emits `onboarding_started` once the session
 * queries (account + company) have answered — proving the app actually runs
 * in the prospect's browser (the delta with the backend `autologin_succeeded`
 * exposes the webview black hole). sessionStorage rather than a module flag
 * so the mark survives a webview-initiated reload; access is try/catched
 * because storage can throw (Safari private mode).
 */
const AUTOLOGIN_PENDING_KEY = "mytour.analytics.autologin-pending";

export function markAutologinPending(): void {
  try {
    sessionStorage.setItem(AUTOLOGIN_PENDING_KEY, "1");
  } catch {
    // Storage unavailable — we lose one funnel event, never the login.
  }
}

function consumeAutologinPending(): boolean {
  try {
    if (sessionStorage.getItem(AUTOLOGIN_PENDING_KEY) === null) {
      return false;
    }
    sessionStorage.removeItem(AUTOLOGIN_PENDING_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Mounted once at the app shell (AnalyticsContainer, next to
 * SentryContainer). Identifies the PostHog person, emits the onboarding
 * funnel milestones on signal transitions, and scopes session replay to
 * accounts still in onboarding.
 */
export function useAnalyticsSession(): void {
  const {account} = useAccount();
  const {company} = useCurrentCompany();
  const {platformUserStyle} = useCurrentUserStyle();
  /**
   * Modération (support qui impersonate un client) : jamais de tracking réel
   * ni de replay — le compte chargé est celui du client, un support ne doit
   * ni flip les milestones, ni démarrer un replay. Le garde-fou *structurel*
   * vit dans `analyticsService` (track/startSessionReplay no-op, identify
   * routé vers le flags-only path) ; les checks `isModeration` ci-dessous ne
   * sont donc plus la frontière d'émission mais gardent chacun leur utilité :
   * - effet identify : le call à `analyticsService.identify` tourne QUAND
   *   MÊME en modération — le service le route vers le chemin non-capturant,
   *   pour que les flags ciblés par person property suivent le client ; seul
   *   `consumeAutologinPending()` reste gardé, car c'est une mutation
   *   sessionStorage — la consommer en modération perdrait le flag pour la
   *   vraie session du client.
   * - effet milestones : évite de calculer le snapshot (isLegalInfoComplete,
   *   isReadyToInvoice…) pour rien.
   * - effet replay : coupe un replay déjà lancé si la modération démarre en
   *   cours de session (`stopSessionReplay` n'est volontairement pas gardé
   *   côté service).
   * Le backend exclut aussi la modération de ses events de transport ; le
   * front applique la même exclusion.
   */
  const {isModeration} = useModeration();

  const identifyFingerprintRef = useRef<string | null>(null);
  const snapshotRef = useRef<OnboardingSnapshot | null>(null);
  const emittedMilestonesRef = useRef<Set<OnboardingMilestoneEvent>>(new Set());
  const replayStartedRef = useRef(false);

  // Identify + onboarding_started — needs account and company only.
  useEffect(() => {
    if (!account || !company) {
      return;
    }
    const personProperties: AnalyticsPersonProperties = {
      companyId: company.id,
      plan: company.subscription.plan,
      companyType: company.legal.companyType ?? undefined,
      isRedevable: !isEmptyVatId(company.legal.vatId),
      jobCategory: company.features.jobCategory ?? undefined,
      onboarding: account.onboarding,
      customersCount: account.stats.customers,
      serviceDescriptionsCount: account.stats.serviceDescriptions,
    };
    const fingerprint = `${account.id}:${JSON.stringify(personProperties)}`;
    if (identifyFingerprintRef.current !== fingerprint) {
      identifyFingerprintRef.current = fingerprint;
      analyticsService.identify(account.id, personProperties);
    }
    if (!isModeration && consumeAutologinPending()) {
      analyticsService.track("onboarding_started", {companyId: company.id});
    }
  }, [isModeration, account, company]);

  // Milestones — gated on account + company only. The logo signal stays
  // `null` until the style query answers: blocking the whole snapshot on it
  // would lose any milestone acquired while the style request is in flight
  // (and a failing style endpoint would silently kill the funnel).
  useEffect(() => {
    if (isModeration || !account || !company) {
      return;
    }
    const next: OnboardingSnapshot = {
      customers: account.stats.customers,
      serviceDescriptions: account.stats.serviceDescriptions,
      legalComplete: isLegalInfoComplete(company),
      hasLogo: platformUserStyle ? Boolean(platformUserStyle.logoHorizontalThumbnail) : null,
      readyToInvoice: isReadyToInvoice(account, company),
    };
    for (const event of computeOnboardingMilestones(snapshotRef.current, next)) {
      // One-shot per session: a "first_*" milestone must fire once even if the
      // user regresses and re-acquires the signal in the same session (create
      // first customer → delete all → create again replays the 0 → 1
      // transition). The pure detector stays pure; the de-dup lives here.
      if (emittedMilestonesRef.current.has(event)) {
        continue;
      }
      emittedMilestonesRef.current.add(event);
      analyticsService.track(event, {companyId: company.id});
    }
    snapshotRef.current = next;
  }, [isModeration, account, company, platformUserStyle]);

  // Session replay, scoped: record only while a non-moderation account is in
  // onboarding. Computed as a single `shouldRecord` so that a mid-session flip
  // (onboarding ends, OR moderation turns on while a replay is already running)
  // stops the recording — an early-return on isModeration would leave an active
  // replay running through a support session.
  useEffect(() => {
    if (!account) {
      return;
    }
    const shouldRecord = !isModeration && account.onboarding;
    if (shouldRecord && !replayStartedRef.current) {
      replayStartedRef.current = true;
      analyticsService.startSessionReplay();
    } else if (!shouldRecord && replayStartedRef.current) {
      replayStartedRef.current = false;
      analyticsService.stopSessionReplay();
    }
  }, [isModeration, account]);
}
